/**
 * Supabase Icon Workaround for RLS Permission Issues
 * 
 * Since the anon key can't list buckets but can access public files,
 * we'll maintain a static list of known icons and test their availability.
 */

export interface IconFile {
  name: string;
  category: 'avatar' | 'badge' | 'ui' | 'animation' | 'state' | 'functional';
  description: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

// Static list of all known icons from your Supabase bucket
export const KNOWN_SUPABASE_ICONS: IconFile[] = [
  // Avatars and Characters
  { name: 'lyra-avatar.png', category: 'avatar', description: 'Lyra AI Learning Guide avatar' },
  { name: 'lyra-celebrating.png', category: 'avatar', description: 'Lyra celebrating achievement' },
  { name: 'lyra-thinking.png', category: 'avatar', description: 'Lyra in thinking pose' },
  { name: 'loading-lyra.png', category: 'avatar', description: 'Lyra loading animation' },
  
  // Achievements and Badges
  { name: 'achievement-trophy.png', category: 'badge', description: 'Achievement trophy icon' },
  { name: 'badge-course-complete.png', category: 'badge', description: 'Course completion badge' },
  { name: 'badge-first-chapter.png', category: 'badge', description: 'First chapter completion badge' },
  
  // UI and Interface Icons
  { name: 'communication.png', category: 'ui', description: 'Communication and messaging icon' },
  { name: 'data-analytics.png', category: 'ui', description: 'Data analytics and charts icon' },
  { name: 'learning-target.png', category: 'ui', description: 'Learning objectives target icon' },
  { name: 'growth-plant.png', category: 'ui', description: 'Growth and development plant icon' },
  { name: 'mission-heart.png', category: 'ui', description: 'Mission and values heart icon' },
  { name: 'navbar-logo.png', category: 'ui', description: 'Navigation bar logo' },
  { name: 'hero-main.png', category: 'ui', description: 'Main hero section image' },
  { name: 'chapter-progress.png', category: 'ui', description: 'Chapter progress indicator' },
  
  // State Icons
  { name: 'empty-state-welcome.png', category: 'state', description: 'Welcome empty state illustration' },
  { name: 'error-state-friendly.png', category: 'state', description: 'Friendly error state illustration' },
  
  // Functional Icons
  { name: 'dashboard-meditation.png', category: 'functional', description: 'Dashboard meditation/focus icon' },
];

// Base URL for direct Supabase storage access
const SUPABASE_STORAGE_BASE_URL = 'https://hfkzwjnlxrwynactcmpe.supabase.co/storage/v1/object/public/app-icons';

/**
 * Generate direct public URL for an icon
 */
export const getIconUrl = (iconName: string): string => {
  return `${SUPABASE_STORAGE_BASE_URL}/${iconName}`;
};

/**
 * Test if an icon is accessible by attempting to load it
 */
export const testIconAvailability = async (iconName: string): Promise<boolean> => {
  try {
    const url = getIconUrl(iconName);
    const response = await fetch(url, { method: 'HEAD' });
    return response.ok;
  } catch {
    return false;
  }
};

/**
 * Get all available icons by testing each one
 */
export const getAvailableIcons = async (): Promise<IconFile[]> => {
  const availableIcons: IconFile[] = [];
  
  for (const icon of KNOWN_SUPABASE_ICONS) {
    const isAvailable = await testIconAvailability(icon.name);
    if (isAvailable) {
      availableIcons.push(icon);
    }
  }
  
  return availableIcons;
};

/**
 * Get icons by category
 */
export const getIconsByCategory = (category: IconFile['category']): IconFile[] => {
  return KNOWN_SUPABASE_ICONS.filter(icon => icon.category === category);
};

/**
 * Find icon by name
 */
export const findIcon = (name: string): IconFile | undefined => {
  return KNOWN_SUPABASE_ICONS.find(icon => icon.name === name);
};

/**
 * Get semantic icon mapping for easy use
 */
export const SEMANTIC_ICON_MAP = {
  // Avatars
  'mentor': 'lyra-avatar.png',
  'lyra': 'lyra-avatar.png',
  'celebration': 'lyra-celebrating.png',
  'thinking': 'lyra-thinking.png',
  'loading': 'loading-lyra.png',
  
  // Learning & Education
  'target': 'learning-target.png',
  'learning': 'learning-target.png',
  'education': 'learning-target.png',
  
  // Communication
  'message': 'communication.png',
  'communication': 'communication.png',
  'chat': 'communication.png',
  
  // Achievement
  'trophy': 'achievement-trophy.png',
  'achievement': 'achievement-trophy.png',
  'success': 'achievement-trophy.png',
  'complete': 'badge-course-complete.png',
  'badge': 'badge-first-chapter.png',
  
  // Analytics & Data
  'analytics': 'data-analytics.png',
  'data': 'data-analytics.png',
  'chart': 'data-analytics.png',
  
  // Growth & Development
  'growth': 'growth-plant.png',
  'development': 'growth-plant.png',
  'plant': 'growth-plant.png',
  
  // Mission & Values
  'heart': 'mission-heart.png',
  'mission': 'mission-heart.png',
  'values': 'mission-heart.png',
  
  // Navigation
  'logo': 'navbar-logo.png',
  'brand': 'navbar-logo.png',
  
  // Progress
  'progress': 'chapter-progress.png',
  'chapter': 'chapter-progress.png',
  
  // States
  'welcome': 'empty-state-welcome.png',
  'empty': 'empty-state-welcome.png',
  'error': 'error-state-friendly.png',
  'friendly': 'error-state-friendly.png',
  
  // Functional
  'meditation': 'dashboard-meditation.png',
  'focus': 'dashboard-meditation.png',
  'dashboard': 'dashboard-meditation.png'
} as const;

/**
 * Get icon by semantic name
 */
export const getSemanticIcon = (semantic: keyof typeof SEMANTIC_ICON_MAP): string => {
  return SEMANTIC_ICON_MAP[semantic];
};

/**
 * Preload critical icons for better performance
 */
export const preloadCriticalIcons = async (): Promise<void> => {
  const criticalIcons = [
    'lyra-avatar.png',
    'learning-target.png',
    'communication.png',
    'achievement-trophy.png',
    'navbar-logo.png'
  ];
  
  const preloadPromises = criticalIcons.map(iconName => {
    return new Promise<void>((resolve) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => resolve(); // Don't fail on single icon errors
      img.src = getIconUrl(iconName);
    });
  });
  
  await Promise.all(preloadPromises);
};